import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_STATIC,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_AI_VEHICLE,
} from '../core/PhysicsWorld';

export interface ParkingEvaluationResult {
  isParked: boolean;
  distanceToCurbCm: number;
  parallelAngleDegrees: number;
  isWithinBayLength: boolean;
  curbMounted: boolean;
  score: number; // 0 to 100
  passed: boolean;
  feedback: string;
}

export class ParkingZones {
  public group: THREE.Group;
  public parallelBayCenter = new THREE.Vector3(50, 0, -35);
  public bayParkingCenter = new THREE.Vector3(25, 0, -66);
  public emergencyBrakeStart = new THREE.Vector3(50, 0, 0);
  public wendenZoneCenter = new THREE.Vector3(-50, 0, -35);

  // Obstacle vehicles in parallel bay
  public frontCarBody!: CANNON.Body;
  public rearCarBody!: CANNON.Body;

  constructor(private physicsWorld: PhysicsWorld) {
    this.group = new THREE.Group();
    this.buildParallelParkingZone();
    this.buildBayParkingZone();
    this.buildEmergencyBrakingTrack();
    this.buildWendenZone();
  }

  private buildParallelParkingZone() {
    const zoneGroup = new THREE.Group();
    zoneGroup.position.copy(this.parallelBayCenter);

    // 1. Raised Concrete Sidewalk & Curb (0.12m height)
    const curbMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, roughness: 0.8 });
    const curbMesh = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.14, 28), curbMat);
    curbMesh.position.set(2.4, 0.07, 0);
    curbMesh.receiveShadow = true;

    const sidewalkMat = new THREE.MeshStandardMaterial({ color: 0x64748b, roughness: 0.9 });
    const sidewalkMesh = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.12, 28), sidewalkMat);
    sidewalkMesh.position.set(3.9, 0.06, 0);

    zoneGroup.add(curbMesh, sidewalkMesh);

    // Physical Curb collision body
    const curbBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      material: this.physicsWorld.obstacleMaterial,
      collisionFilterGroup: COLLISION_GROUP_STATIC,
      collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
    });
    curbBody.addShape(new CANNON.Box(new CANNON.Vec3(0.15, 0.07, 14)));
    curbBody.position.set(this.parallelBayCenter.x + 2.4, 0.07, this.parallelBayCenter.z);
    this.physicsWorld.world.addBody(curbBody);

    // 2. White Parking Bay Markings (DIN 1451)
    const lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    // Bay length = 7.0m, Bay width = 2.2m
    const bayLength = 7.0;
    const bayWidth = 2.0;

    const bayOutline = new THREE.Mesh(new THREE.PlaneGeometry(bayWidth, bayLength), new THREE.MeshBasicMaterial({
      color: 0x3b82f6,
      transparent: true,
      opacity: 0.15,
    }));
    bayOutline.rotation.x = -Math.PI / 2;
    bayOutline.position.set(1.2, 0.015, 0);
    zoneGroup.add(bayOutline);

    // 3. Parked Obstacle Cars (Front & Rear)
    this.createParkedCar(zoneGroup, new THREE.Vector3(1.2, 0, 6.2), 0x1e3a8a, true);
    this.createParkedCar(zoneGroup, new THREE.Vector3(1.2, 0, -6.2), 0x991b1b, false);

    this.group.add(zoneGroup);
  }

  private createParkedCar(parent: THREE.Group, pos: THREE.Vector3, colorHex: number, isFront: boolean) {
    const carMat = new THREE.MeshStandardMaterial({ color: colorHex, metalness: 0.7, roughness: 0.3 });
    const carMesh = new THREE.Group();

    const body = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.6, 4.2), carMat);
    body.position.y = 0.45;
    body.castShadow = true;

    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.6, 2.3), carMat);
    cabin.position.set(0, 0.95, -0.2);
    cabin.castShadow = true;

    carMesh.add(body, cabin);
    carMesh.position.copy(pos);
    parent.add(carMesh);

    // Physics body
    const carBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      material: this.physicsWorld.obstacleMaterial,
      collisionFilterGroup: COLLISION_GROUP_STATIC,
      collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
    });
    carBody.addShape(new CANNON.Box(new CANNON.Vec3(0.9, 0.6, 2.1)));
    carBody.position.set(this.parallelBayCenter.x + pos.x, 0.6, this.parallelBayCenter.z + pos.z);
    this.physicsWorld.world.addBody(carBody);

    if (isFront) this.frontCarBody = carBody;
    else this.rearCarBody = carBody;
  }

  private buildBayParkingZone() {
    const bayGroup = new THREE.Group();
    bayGroup.position.copy(this.bayParkingCenter);

    // Asphalt parking bay apron connecting to the z = -60 street
    const apronMat = new THREE.MeshStandardMaterial({
      color: 0x161b22,
      roughness: 0.86,
      metalness: 0.08,
    });
    const apronMesh = new THREE.Mesh(new THREE.PlaneGeometry(12.5, 6.5), apronMat);
    apronMesh.rotation.x = -Math.PI / 2;
    apronMesh.position.set(0, 0.008, 0.4);
    apronMesh.receiveShadow = true;
    bayGroup.add(apronMesh);

    // 4 90-degree parking stalls
    const lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    for (let i = -2; i <= 2; i++) {
      const line = new THREE.Mesh(new THREE.PlaneGeometry(0.12, 5.2), lineMat);
      line.rotation.x = -Math.PI / 2;
      line.position.set(i * 2.6, 0.015, 0);
      bayGroup.add(line);
    }

    // Target stall highlight
    const targetBay = new THREE.Mesh(new THREE.PlaneGeometry(2.48, 5.0), new THREE.MeshBasicMaterial({
      color: 0x10b981,
      transparent: true,
      opacity: 0.2,
    }));
    targetBay.rotation.x = -Math.PI / 2;
    targetBay.position.set(0, 0.012, 0);
    bayGroup.add(targetBay);

    this.group.add(bayGroup);
  }

  private buildEmergencyBrakingTrack() {
    const trackGroup = new THREE.Group();
    trackGroup.position.copy(this.emergencyBrakeStart);

    // Straight 120m asphalt sprint track
    const lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const startLine = new THREE.Mesh(new THREE.PlaneGeometry(3.5, 0.4), lineMat);
    startLine.rotation.x = -Math.PI / 2;
    startLine.position.set(0, 0.015, 0);

    const brakeTriggerLine = new THREE.Mesh(new THREE.PlaneGeometry(3.5, 0.4), new THREE.MeshBasicMaterial({ color: 0xef4444 }));
    brakeTriggerLine.rotation.x = -Math.PI / 2;
    brakeTriggerLine.position.set(0, 0.015, 45); // Trigger line at 45m

    trackGroup.add(startLine, brakeTriggerLine);
    this.group.add(trackGroup);
  }

  private buildWendenZone() {
    const wendenGroup = new THREE.Group();
    wendenGroup.position.copy(this.wendenZoneCenter);

    // Narrow 5.5m German residential street between curbs
    const curbMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8 });
    const curbL = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.14, 30), curbMat);
    curbL.position.set(-3.0, 0.07, 0);

    const curbR = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.14, 30), curbMat);
    curbR.position.set(3.0, 0.07, 0);

    wendenGroup.add(curbL, curbR);
    this.group.add(wendenGroup);
  }

  public evaluateParallelParking(carPos: THREE.Vector3, carQuat: THREE.Quaternion, speedKmh: number): ParkingEvaluationResult {
    const isStopped = Math.abs(speedKmh) < 0.2;
    // Local coords relative to parallel bay center
    const localX = carPos.x - this.parallelBayCenter.x;
    const localZ = carPos.z - this.parallelBayCenter.z;

    // Curb is at x = 2.4
    // Car right side is at localX + 0.9
    const carRightEdgeX = localX + 0.9;
    const distanceToCurbMeters = 2.4 - carRightEdgeX;
    const distanceToCurbCm = Math.round(Math.max(0, distanceToCurbMeters * 100));

    // Angle relative to Z axis
    const euler = new THREE.Euler().setFromQuaternion(carQuat, 'YXZ');
    let angleDeg = Math.abs((euler.y * 180) / Math.PI);
    while (angleDeg > 90) angleDeg = Math.abs(angleDeg - 180);

    const isWithinBayLength = Math.abs(localZ) < 3.2;
    const curbMounted = distanceToCurbMeters < -0.02;

    let passed = false;
    let score = 0;
    let feedback = '';

    if (curbMounted) {
      feedback = 'FEHLER: Auf den Bordstein aufgefahren! (Sofortiges Durchfallen)';
      score = 0;
    } else if (!isWithinBayLength) {
      feedback = 'Fahrzeug steht nicht vollständig in der Parklücke.';
      score = 20;
    } else if (distanceToCurbCm > 45) {
      feedback = `Zu weit vom Bordstein entfernt (${distanceToCurbCm} cm > 30 cm zulässig).`;
      score = 45;
    } else if (angleDeg > 8.0) {
      feedback = `Fahrzeug steht schief (${Math.round(angleDeg)}° > 5° zulässig).`;
      score = 60;
    } else if (distanceToCurbCm <= 30 && angleDeg <= 6.0 && isStopped) {
      passed = true;
      score = 100;
      feedback = `Perfekt eingeparkt! Bordsteinabstand: ${distanceToCurbCm} cm, Ausrichtung: ${Math.round(angleDeg)}°.`;
    } else {
      score = 75;
      feedback = `Gut eingeparkt, noch leicht optimierbar (Abstand: ${distanceToCurbCm} cm).`;
    }

    return {
      isParked: isStopped && isWithinBayLength,
      distanceToCurbCm,
      parallelAngleDegrees: Math.round(angleDeg * 10) / 10,
      isWithinBayLength,
      curbMounted,
      score,
      passed,
      feedback,
    };
  }

  public evaluateBayParking(carPos: THREE.Vector3, carQuat: THREE.Quaternion, speedKmh: number): ParkingEvaluationResult {
    const isStopped = Math.abs(speedKmh) < 0.2;
    const localX = carPos.x - this.bayParkingCenter.x;
    const localZ = carPos.z - this.bayParkingCenter.z;

    const lateralOffsetCm = Math.round(Math.abs(localX) * 100);
    const isWithinBayLength = Math.abs(localZ) <= 1.2 && Math.abs(localX) <= 0.85;

    const euler = new THREE.Euler().setFromQuaternion(carQuat, 'YXZ');
    let angleDeg = Math.abs((euler.y * 180) / Math.PI);
    while (angleDeg > 90) angleDeg = Math.abs(angleDeg - 180);

    let passed = false;
    let score = 0;
    let feedback = '';

    if (!isWithinBayLength) {
      feedback = 'Fahrzeug steht noch nicht vollständig in der markierten Parkbucht.';
      score = 20;
    } else if (lateralOffsetCm > 45) {
      feedback = `Nicht mittig in der Parkbucht (Abweichung: ${lateralOffsetCm} cm > 45 cm zulässig).`;
      score = 50;
    } else if (angleDeg > 7.0) {
      feedback = `Fahrzeug steht schief in der Parkbucht (${Math.round(angleDeg)}° > 7° zulässig).`;
      score = 60;
    } else if (lateralOffsetCm <= 45 && angleDeg <= 7.0 && isStopped) {
      passed = true;
      score = Math.max(80, 100 - Math.round(lateralOffsetCm * 0.3));
      feedback = `Querparken perfekt abgeschlossen! Abweichung zur Mitte: ${lateralOffsetCm} cm, Winkel: ${Math.round(angleDeg)}°.`;
    } else {
      score = 75;
      feedback = `In der Parkbucht, bitte bis zum vollständigen Stillstand anhalten.`;
    }

    return {
      isParked: isStopped && isWithinBayLength,
      distanceToCurbCm: lateralOffsetCm,
      parallelAngleDegrees: Math.round(angleDeg * 10) / 10,
      isWithinBayLength,
      curbMounted: false,
      score,
      passed,
      feedback,
    };
  }

  public evaluateThreePointTurn(
    carPos: THREE.Vector3,
    carQuat: THREE.Quaternion,
    speedKmh: number,
    initialForwardZ: number,
    reverseCount: number
  ): { completed: boolean; curbHit: boolean; passed: boolean; score: number; feedback: string } {
    const localX = carPos.x - this.wendenZoneCenter.x;
    const localZ = carPos.z - this.wendenZoneCenter.z;

    // Curbs are at localX = ±3.0m (inner face at ±2.85m), car half-width ~0.91m
    const curbHit = Math.abs(localX) > 2.05 && Math.abs(localZ) < 15.0;

    const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(carQuat).normalize();
    // Opposite heading check: dot product with initial forward Z <= -0.906 (~155°–180° turn completed)
    const headingReversed = forward.z * initialForwardZ <= -0.906;
    const inZone = Math.abs(localZ) < 15.0 && Math.abs(localX) <= 2.05;

    if (curbHit) {
      return {
        completed: true,
        curbHit: true,
        passed: false,
        score: 0,
        feedback: 'FEHLER: Beim Wenden auf den Bordstein aufgefahren!',
      };
    }

    if (headingReversed && reverseCount >= 1 && inZone && speedKmh > 2.0) {
      const score = reverseCount <= 2 ? 100 : 80;
      return {
        completed: true,
        curbHit: false,
        passed: true,
        score,
        feedback: `Umkehren in ${reverseCount + 1} Zügen sicher und ohne Bordsteinkontakt abgeschlossen!`,
      };
    }

    return {
      completed: false,
      curbHit: false,
      passed: false,
      score: 0,
      feedback: 'Wenden läuft...',
    };
  }
}
